import React, { useEffect, useMemo, useState } from "react";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../firebase";
import { useCuerpo } from "../context/CuerpoContext";

const BANNER_CACHE_TTL_MS = 10 * 60 * 1000;

function leerBannerCache(key) {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.data)) return null;
    if (Date.now() - Number(parsed.savedAt || 0) > BANNER_CACHE_TTL_MS) {
      sessionStorage.removeItem(key);
      return null;
    }
    return parsed.data;
  } catch (_) {
    return null;
  }
}

function guardarBannerCache(key, data) {
  try {
    sessionStorage.setItem(
      key,
      JSON.stringify({ savedAt: Date.now(), data })
    );
  } catch (_) {}
}

function valorFechaMs(valor) {
  if (!valor) return 0;
  if (typeof valor?.toDate === "function") return valor.toDate().getTime();
  if (typeof valor?.seconds === "number") return valor.seconds * 1000;
  if (typeof valor?._seconds === "number") return valor._seconds * 1000;
  const t = new Date(valor).getTime();
  return Number.isNaN(t) ? 0 : t;
}

/**
 * Banner de texto bajo el logo.
 * - Muestra SOLO el banner activo por fecha/hora.
 * - Si se pasa `cia`, filtra por esa compañía (si el banner tiene campo `cia`).
 *   Los banners sin `cia` se consideran "globales" y también se muestran.
 */
export default function TextBannerUnderLogo({ refreshMs = 60000, cia }) {
  const { cuerpoId } = useCuerpo();
  const [items, setItems] = useState([]);
  const [now, setNow] = useState(() => new Date());

  // Ticker para reevaluar el rango horario
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), refreshMs);
    return () => clearInterval(timer);
  }, [refreshMs]);

  // Carga de banners: cache inmediato + actualización Firestore en segundo plano.
  useEffect(() => {
    let mounted = true;

    const aplicar = (docs) => {
      const filtered = docs
        .filter((it) => it.enabled !== false)
        .filter((it) => !cia || !it.cia || it.cia === cia);

      filtered.sort((a, b) => valorFechaMs(a.startAt) - valorFechaMs(b.startAt));
      if (mounted) setItems(filtered);
    };

    (async () => {
      try {
        if (!cuerpoId) {
          setItems([]);
          return;
        }

        const cacheKey = `bes:textBanners:${cuerpoId}`;
        const cached = leerBannerCache(cacheKey);
        if (cached) aplicar(cached);

        const q = query(
          collection(db, "textBanners"),
          where("cuerpo_id", "==", cuerpoId)
        );
        const snap = await getDocs(q);
        if (!mounted) return;

        const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        guardarBannerCache(cacheKey, docs);
        aplicar(docs);
      } catch (err) {
        console.error("Error leyendo textBanners:", err);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [cia, cuerpoId]);

  // Decide el banner activo por rango horario (y respeta "order")
  const active = useMemo(() => {
    const t = now.getTime();
    const actives = items.filter((it) => {
      const s = valorFechaMs(it.startAt);
      const e = valorFechaMs(it.endAt);
      return t >= s && t < e;
    });
    actives.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    return actives[0] || null;
  }, [items, now]);

  if (!active) return null;

  // Colores (con fallback)
  const titleColor      = active.titleColor || "#16a34a";
  const obacColor       = active.obacColor  || "#dc2626";
  const maquinistaColor = active.maquinistaColor || obacColor || "#dc2626";
  const bomberosColor   = active.bomberosColor   || obacColor || "#dc2626";

  return (
    <div
      style={{
        width: "100%",
        display: "flex",
        justifyContent: "center",
        marginTop: 8,
        padding: "0 12px",
      }}
    >
      <div
        style={{
          background: "rgba(0,0,0,0.06)",
          borderRadius: 10,
          padding: "10px 14px",
          display: "inline-flex",
          alignItems: "center",
          gap: 14,
          flexWrap: "wrap",
          fontSize: "clamp(14px, 2vw, 18px)",
          lineHeight: 1.35,
          textAlign: "center",
        }}
      >
        {/* Título en color configurado */}
        <span style={{ fontWeight: 800, color: titleColor }}>{active.title}</span>

        {/* OBAC: solo el rótulo en rojo/negrita; el nombre normal */}
        {active.obac && (
          <span>
            <strong style={{ color: obacColor }}>OBAC:</strong>{" "}
            <span style={{ fontWeight: 400, color: "inherit" }}>{active.obac}</span>
          </span>
        )}

        {/* Maquinista */}
        {active.maquinista && (
          <span>
            <strong style={{ color: maquinistaColor }}>Maquinista:</strong>{" "}
            <span style={{ fontWeight: 400, color: "inherit" }}>{active.maquinista}</span>
          </span>
        )}

        {/* Bomberos */}
        {Array.isArray(active.bomberos) && active.bomberos.length > 0 && (
          <span>
            <strong style={{ color: bomberosColor }}>Bomberos:</strong>{" "}
            <span style={{ fontWeight: 400, color: "inherit" }}>
              {active.bomberos.join(", ")}
            </span>
          </span>
        )}
      </div>
    </div>
  );
}
